import { timesEqual, normalizeTime } from './datetime';

function countDaysMatching(days, predicate) {
  return days.filter(predicate).length;
}

function findDays(days, predicate) {
  return days.filter(predicate);
}

function dayDateKey(day) {
  if (!day?.date) return '';
  const s = String(day.date);
  return s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
}

function summarizeDay(day) {
  return {
    date: dayDateKey(day),
    mode: day.mode,
    paidBreakPaid: day.paidBreakPaid,
    paidBreakMinutes: day.paidBreakMinutes,
    unpaidBreakMinutes: day.unpaidBreakMinutes,
    actualBreakMinutes: day.actualBreakMinutes,
    totalHours: day.totalHours,
    paidIn: day.paidIn,
    paidOut: day.paidOut,
    scheduledIn: day.scheduledIn,
    scheduledOut: day.scheduledOut,
    actualIn: day.actualIn,
    actualOut: day.actualOut,
  };
}

function describeUnmatchedDays(days, row) {
  const dated = row.date ? days.filter((day) => dayDateKey(day) === row.date) : days;
  if (row.date && dated.length === 0) {
    const dates = [...new Set(days.map(dayDateKey))].filter(Boolean);
    return `No row for date ${row.date} (timecard dates: ${dates.join(', ') || 'none'})`;
  }
  return `Actual row(s)${row.date ? ` for ${row.date}` : ''}: ${JSON.stringify(dated.map(summarizeDay))}`;
}

function assertTimecardDays(days, expectations, errors) {
  if (expectations.minDayCount != null && days.length < expectations.minDayCount) {
    errors.push(`expected at least ${expectations.minDayCount} day row(s), got ${days.length}`);
  }
  if (expectations.exactDayCount != null && days.length !== expectations.exactDayCount) {
    errors.push(`expected exactly ${expectations.exactDayCount} day row(s), got ${days.length}`);
  }

  for (const row of expectations.rows || []) {
    const matches = days.filter((day) => {
      if (row.date && dayDateKey(day) !== row.date) return false;
      if (row.mode && day.mode !== row.mode) return false;
      if (row.hasActuals === true && (!day.actualIn || !day.actualOut)) return false;
      if (row.hasActuals === false && (day.actualIn || day.actualOut)) return false;
      if (row.missingActualOut === true && day.actualOut) return false;
      if (row.scheduledIn && !timesEqual(day.scheduledIn, row.scheduledIn)) return false;
      if (row.scheduledOut && !timesEqual(day.scheduledOut, row.scheduledOut)) return false;
      if (row.actualIn && !timesEqual(day.actualIn, row.actualIn)) return false;
      if (row.actualOut && !timesEqual(day.actualOut, row.actualOut)) return false;
      if (row.syncErrorMessage && day.syncErrorMessage !== row.syncErrorMessage) return false;
      if (row.syncErrorMessage === null && day.syncErrorMessage) return false;
      if (row.totalHours != null && Number(day.totalHours) !== Number(row.totalHours)) return false;
      if (row.paidInNull === true && day.paidIn != null) return false;
      if (row.paidOutNull === true && day.paidOut != null) return false;
      if (row.paidInMatchesActual === true && !timesEqual(day.paidIn, day.actualIn)) return false;
      if (row.paidOutMatchesActual === true && !timesEqual(day.paidOut, day.actualOut)) return false;
      if (row.noSchedule === true && (day.scheduledIn || day.scheduledOut)) return false;
      if (row.actualBreakMinutes != null && Number(day.actualBreakMinutes) !== Number(row.actualBreakMinutes)) return false;
      if (row.paidBreakMinutes != null && Number(day.paidBreakMinutes) !== Number(row.paidBreakMinutes)) return false;
      if (row.unpaidBreakMinutes != null && Number(day.unpaidBreakMinutes) !== Number(row.unpaidBreakMinutes)) return false;
      if (row.paidBreakPaid === true && !day.paidBreakPaid) return false;
      if (row.paidBreakPaid === false && day.paidBreakPaid) return false;
      if (row.paidInEquals && !timesEqual(day.paidIn, row.paidInEquals)) return false;
      if (row.paidOutEquals && !timesEqual(day.paidOut, row.paidOutEquals)) return false;
      if (row.paidInNotEquals && timesEqual(day.paidIn, row.paidInNotEquals)) return false;
      return true;
    });

    if (row.count != null) {
      if (matches.length !== row.count) {
        errors.push(
          `expected ${row.count} row(s) matching ${JSON.stringify(row)}, found ${matches.length}. ${describeUnmatchedDays(days, row)}`
        );
      }
    } else if (matches.length === 0) {
      errors.push(`no timecard day matched ${JSON.stringify(row)}. ${describeUnmatchedDays(days, row)}`);
    }
  }
}

function assertStep4Records(step4Records, expectations, errors) {
  if (expectations.withActualsCount != null) {
    const withActuals = step4Records.filter((r) => r.actualInDateTime != null);
    if (withActuals.length !== expectations.withActualsCount) {
      errors.push(`step4 withActualsCount: expected ${expectations.withActualsCount}, got ${withActuals.length}`);
    }
  }
  if (expectations.withoutActualsCount != null) {
    const without = step4Records.filter((r) => r.actualInDateTime == null);
    if (without.length !== expectations.withoutActualsCount) {
      errors.push(`step4 withoutActualsCount: expected ${expectations.withoutActualsCount}, got ${without.length}`);
    }
  }
}

function assertStep5Records(step5Records, expectations, errors) {
  if (expectations.count != null && step5Records.length !== expectations.count) {
    errors.push(`step5 count: expected ${expectations.count}, got ${step5Records.length}`);
  }
}

function assertLocationTotals(totals, expectations, errors) {
  for (const exp of expectations.rows || []) {
    const match = totals.find((t) => t.locationId === exp.locationId);
    if (!match) {
      errors.push(`location-totals: no entry for locationId ${exp.locationId}`);
      continue;
    }
    if (exp.totalHours != null && Number(match.totalHours) !== Number(exp.totalHours)) {
      errors.push(
        `location-totals loc ${exp.locationId}: expected totalHours ${exp.totalHours}, got ${match.totalHours}`
      );
    }
    if (exp.minTotalHours != null && Number(match.totalHours) < Number(exp.minTotalHours)) {
      errors.push(
        `location-totals loc ${exp.locationId}: expected totalHours >= ${exp.minTotalHours}, got ${match.totalHours}`
      );
    }
  }
  if (expectations.minCount != null && totals.length < expectations.minCount) {
    errors.push(`location-totals: expected at least ${expectations.minCount} entries, got ${totals.length}`);
  }
}

export {
  assertTimecardDays,
  assertStep4Records,
  assertStep5Records,
  assertLocationTotals,
  countDaysMatching,
  findDays,
  summarizeDay,
  describeUnmatchedDays,
  normalizeTime,
};
